/* Petrol Post — Clarity cart events.
 *
 * Answers one question: when a past drop is bought WITHOUT the membership,
 * did the shopper never add the membership, or did they add it and remove it?
 * Shopify orders cannot tell those apart, because a removed line is not stored.
 *
 * Method: wrap fetch, and after every cart mutation re-read /cart.js and diff.
 * Deliberately NOT bound to any button or DOM class, so a theme or upsell
 * change cannot silently kill it. Shrine replaces the drawer wholesale on every
 * cart change, which is exactly what a DOM listener would not survive.
 *
 * Events fired (Clarity -> Filters, Dashboard, Recordings):
 *   pp_cart_touch                 any cart mutation. HEARTBEAT.
 *   pp_drop_added_with_member     fine: drop attached to a membership.
 *   pp_drop_added_no_member       drop added, membership never in cart.
 *   pp_member_removed             membership taken out of the cart.
 *   pp_member_removed_drop_kept   membership out, drop kept. The worst case.
 *
 * Also, outside the cart:
 *   pp_past_drops_nav_click       clicked "Past Drops" in the nav. The exit door.
 *   pp_yearly_selected            picked the Yearly plan on the PDP.
 *   pp_yearly_then_monthly        picked Yearly, then went back to Monthly.
 *
 * pp_yearly_then_monthly is the near-miss annual. Orders only show what was
 * bought, so an annual that was considered and dropped is invisible today.
 *
 * pp_cart_touch is the liveness proof. Zero of it means this script is broken,
 * which otherwise reads identically to "nobody does this".
 *
 * Added 2026-08-31.
 */
(function () {
  // Double-install guard: a second include would wrap fetch twice and double
  // every event. Cheap insurance against a future theme edit adding the tag again.
  if (window.__ppClarityCartEvents) return;
  window.__ppClarityCartEvents = true;

  var MEMBERSHIP_PRODUCT_ID = 15808724009341;
  var MONTHLY_VARIANT = '57986506719613';
  var YEARLY_VARIANT = '57986506752381';
  var MUTATORS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];
  var SEEN_MEMBER_KEY = 'pp_member_seen';

  function clarity() {
    if (typeof window.clarity !== 'function') return null;
    return window.clarity;
  }

  function fire(name) {
    var c = clarity();
    if (!c) return;
    try { c('event', name); } catch (e) {}
  }

  function tag(key, value) {
    var c = clarity();
    if (!c) return;
    try { c('set', key, String(value)); } catch (e) {}
  }

  // Keeps this session's recording when the interesting thing happens, so the
  // event is not just a number with no video behind it.
  function keepRecording(reason) {
    var c = clarity();
    if (!c) return;
    try { c('upgrade', reason); } catch (e) {}
  }

  function sessionFlag(key, value) {
    try {
      if (value === undefined) return window.sessionStorage.getItem(key) === '1';
      window.sessionStorage.setItem(key, value ? '1' : '0');
      return !!value;
    } catch (e) {}
    return false;
  }

  function summarise(cart) {
    var member = false;
    var drops = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].product_id === MEMBERSHIP_PRODUCT_ID) member = true;
      else drops += items[i].quantity || 0;
    }
    return { member: member, drops: drops };
  }

  var prev = null;

  function compare(next) {
    fire('pp_cart_touch');
    tag('pp_cart_state', (next.member ? 'member' : 'no-member') + '/' + next.drops + '-drops');

    if (next.member) sessionFlag(SEEN_MEMBER_KEY, true);
    var everHadMember = sessionFlag(SEEN_MEMBER_KEY);

    if (prev) {
      if (next.drops > prev.drops) {
        fire(next.member ? 'pp_drop_added_with_member' : 'pp_drop_added_no_member');
      }
      if (prev.member && !next.member) {
        fire('pp_member_removed');
        keepRecording('pp_member_removed');
        if (next.drops > 0) fire('pp_member_removed_drop_kept');
      }
    } else if (next.drops > 0 && !next.member && !everHadMember) {
      // First observation of the session already holds drops and no membership,
      // e.g. a returning cart. Recorded so the denominator is not silently short.
      fire('pp_drop_added_no_member');
    }

    prev = next;
  }

  // Rapid successive cart calls resolve out of order, and a stale reply would
  // overwrite `prev` with older state and emit phantom transitions. Only the
  // newest read is allowed to land.
  var readSeq = 0;

  function readCart() {
    var mine = ++readSeq;
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) { if (mine === readSeq) compare(summarise(c)); })
      .catch(function () {});
  }

  // Never throws. A non-string here would otherwise bubble out of the fetch
  // wrapper AFTER the real call started, and the caller would get an exception
  // instead of its promise, which breaks add-to-cart.
  function isMutator(url) {
    if (typeof url !== 'string' || !url) return false;
    for (var i = 0; i < MUTATORS.length; i++) {
      if (url.indexOf(MUTATORS[i]) !== -1) return true;
    }
    return false;
  }

  // Accepts a string, a Request (.url), or a URL object (.href).
  function urlOf(input) {
    try {
      if (typeof input === 'string') return input;
      if (!input) return '';
      var u = input.url || input.href;
      return typeof u === 'string' ? u : '';
    } catch (e) { return ''; }
  }

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = urlOf(input);
      var call = nativeFetch.apply(this, arguments);
      try {
        if (isMutator(url)) call.then(function () { readCart(); }).catch(function () {});
      } catch (e) {}
      return call;
    };
  }

  // Shrine and some apps still use XHR for cart writes.
  var open = window.XMLHttpRequest && window.XMLHttpRequest.prototype.open;
  if (open) {
    window.XMLHttpRequest.prototype.open = function (method, url) {
      // A reused XHR object would otherwise accumulate one listener per open(),
      // and later NON-mutator requests on it would still fire a cart read.
      // Attach once, then gate on the flag set by the current open().
      this.__ppMutator = isMutator(urlOf({ url: url }));
      if (this.__ppMutator && !this.__ppHooked) {
        this.__ppHooked = true;
        this.addEventListener('load', function () {
          if (this.__ppMutator) readCart();
        });
      }
      return open.apply(this, arguments);
    };
  }

  // ---- Past Drops nav link -------------------------------------------------
  // Bound to the HREF, not a class: the href is the contract, class names are not.
  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('a[href*="past-drops"]') : null;
    if (!el) return;
    fire('pp_past_drops_nav_click');
    var from = 'other';
    if (location.pathname.indexOf('petrol-post-membership') !== -1) from = 'membership-pdp';
    else if (location.pathname === '/') from = 'home';
    tag('pp_past_drops_from', from);
    if (from === 'membership-pdp') keepRecording('pp_past_drops_nav_click');
  }, true);

  // ---- Plan picker ---------------------------------------------------------
  // Reads the product form's variant input, a Shopify convention, rather than
  // the theme's own picker markup.
  var sawYearly = false;
  var lastPlan = null;

  function currentVariant() {
    var input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (input && input.value) return String(input.value);
    var m = location.search.match(/[?&]variant=(\d+)/);
    return m ? m[1] : null;
  }

  function checkPlan() {
    var v = currentVariant();
    if (!v || v === lastPlan) return;
    lastPlan = v;
    if (v === YEARLY_VARIANT) {
      if (!sawYearly) { sawYearly = true; fire('pp_yearly_selected'); }
    } else if (v === MONTHLY_VARIANT && sawYearly) {
      fire('pp_yearly_then_monthly');
      keepRecording('pp_yearly_then_monthly');
    }
  }

  document.addEventListener('change', checkPlan, true);
  document.addEventListener('click', function () { setTimeout(checkPlan, 60); }, true);
  checkPlan();

  readCart();
})();
